import { Component } from 'react';
import { reportFrontendError } from './sentry.js';

// Global Error Boundary. Before this, an unhandled error thrown while rendering
// ANY of the ~77 view components would unmount the whole React tree and leave
// the agent staring at a blank white page — mid-workday, no way to recover
// short of a manual reload, and no signal to us that it happened. React only
// stops the propagation of a render error at a class component that implements
// getDerivedStateFromError / componentDidCatch, so this is deliberately a
// class (there is no hook equivalent).
//
// Behavior: on a render crash we (1) show a calm, bilingual fallback with a
// "Recargar" button instead of the blank screen, and (2) forward the error to
// the same Sentry reporter the global handlers use — an error boundary catch
// does NOT trigger window.onerror, so without this call these crashes would be
// invisible in Sentry. No PHI is included (see sentry.js privacy note).
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: (error && error.message) || 'Error' };
  }

  componentDidCatch(error, info) {
    // Keep the original in the dev console, forward to Sentry in prod.
    console.error('[ErrorBoundary] render crash:', error, info);
    try {
      reportFrontendError(error, 'react.errorBoundary');
    } catch (_) {
      /* never let the reporter itself break the fallback */
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    // Spanish-first to match the primary user; English underneath. Inline
    // styles only — the boundary must render even if the CSS bundle or a
    // theme provider is what failed.
    return (
      <div
        role="alert"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          background: '#0b1220',
          color: '#e2e8f0',
          fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
        }}
      >
        <div style={{ maxWidth: 440, textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>⚠️</div>
          <h1 style={{ fontSize: '1.15rem', fontWeight: 700, margin: '0 0 .5rem' }}>
            Algo salió mal en esta pantalla
          </h1>
          <p style={{ fontSize: '.85rem', color: '#94a3b8', margin: '0 0 .35rem', lineHeight: 1.5 }}>
            No se perdió ninguna información. Recarga la página para continuar; si
            vuelve a pasar, avísanos.
          </p>
          <p style={{ fontSize: '.72rem', color: '#64748b', margin: '0 0 1.25rem', lineHeight: 1.5 }}>
            Something went wrong on this screen. Your data is safe — reload to
            continue.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              background: 'linear-gradient(135deg,#06b6d4,#10b981)',
              color: '#00131a',
              border: 'none',
              borderRadius: 10,
              padding: '.7rem 1.4rem',
              fontSize: '.9rem',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Recargar / Reload
          </button>
        </div>
      </div>
    );
  }
}
